import { Routes } from '@angular/router';
import { authGuard, adminGuard } from './guards/auth.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./components/home/home.component').then(m => m.HomeComponent)
  },
  {
    path: 'login',
    redirectTo: '/',  // Redirect to home which will show login modal
    pathMatch: 'full'
  },
  {
    path: 'register',
    // ⚠️ SECURITY NOTE (AE-4): This route should ideally have a guard checking for invitation token
    // Currently secured in component level (register.component.ts throws error without invitation)
    // Future improvement: Add invitationGuard to prevent unauthorized access
    loadComponent: () => import('./components/auth/register/register').then(m => m.Register)
  },
  {
    path: 'invitation-accept',
    loadComponent: () => import('./components/auth/invitation-acceptance/invitation-acceptance').then(m => m.InvitationAcceptanceComponent)
  },
  {
    path: 'profile',
    canActivate: [authGuard],
    loadComponent: () => import('./components/user/user-profile/user-profile').then(m => m.UserProfileComponent),
    data: { preload: true, preloadPriority: 1 } // ⚡ High priority - frequently accessed
  },
  {
    path: 'data-sharing',
    canActivate: [authGuard],
    loadComponent: () => import('./components/user/data-sharing/data-sharing').then(m => m.DataSharing),
    data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - data sharing page
  },
  {
    path: 'dashboard',
    redirectTo: '/patients',
    pathMatch: 'full'
  },
  {
    path: 'admin',
  canActivate: [authGuard, adminGuard],
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full'
      },
      {
        path: 'overview',
        loadComponent: () => import('./components/admin/admin-dashboard/admin-dashboard').then(m => m.AdminDashboard),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - admin dashboard
      },
      {
        path: 'users',
        loadComponent: () => import('./components/admin/user-management/user-management').then(m => m.UserManagementComponent),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - user management
      },
      {
        path: 'centers',
        loadComponent: () => import('./components/admin/center-management/center-management').then(m => m.CenterManagementComponent),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - center management
      },
      {
        path: 'patients',
        loadComponent: () => import('./components/admin/patient-management/patient-management').then(m => m.PatientManagementComponent),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - patient management (admin)
      }
    ]
  },
  {
    path: 'analytics',
    canActivate: [authGuard],
    loadComponent: () => import('./components/analytics/unified-analytics/unified-analytics').then(m => m.UnifiedAnalytics),
    data: { preload: true, preloadPriority: 1 } // ⚡ High priority - frequently accessed dashboard
  },
  {
    path: 'patients',

    children: [
      {
        path: '',
        loadComponent: () => import('./components/patients/patient-list/patient-list').then(m => m.PatientList),
        data: { preload: true, preloadPriority: 1 } // ⚡ High priority - main patient list
      },
      {
        path: 'new',
        loadComponent: () => import('./components/patients/patient-registration/patient-registration').then(m => m.PatientRegistration),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - patient registration
      },
      {
        path: ':id',
        loadComponent: () => import('./components/patients/patient-detail/patient-detail').then(m => m.PatientDetail),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - patient details
      },
      {
        path: ':id/edit',
        loadComponent: () => import('./components/patients/patient-edit/patient-edit').then(m => m.PatientEdit),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - patient edit
      },
      {
        path: ':patientId/visits/new',
        loadComponent: () => import('./components/visits/visit-registration-v2/visit-registration-v2').then(m => m.VisitRegistrationV2),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - visit registration
      },
      {
        path: ':patientId/visits/:visitId',
        loadComponent: () => import('./components/visits/visit-detail/visit-detail').then(m => m.VisitDetail),
        data: { preload: true, preloadPriority: 2 } // ⚡ Medium priority - visit details
      },
      {
        path: ':patientId/visits/:visitId/summary',
        loadComponent: () => import('./components/visits/visit-summary/visit-summary').then(m => m.VisitSummary)
      }
    ]
  },
  {
    path: 'visits',
    canActivate: [authGuard],
    children: [
      {
        path: 'add',
        loadComponent: () => import('./components/visits/visit-selection/visit-selection').then(m => m.VisitSelection),
        data: { preload: true, preloadPriority: 3 } // ⚡ Low priority - visit selection
      }
      // REMOVED: /visits/register route - use /patients/:patientId/visits/new instead
    ]
  },
  // Documentation Pages
  {
    path: 'dokuman/:type',
    loadComponent: () => import('./components/pages/documentation/documentation').then(m => m.Documentation),
    data: { preload: true, preloadPriority: 3 } // ⚡ Low priority - documentation pages
  },
  {
    path: 'site-haritasi',
    redirectTo: 'dokuman/site-haritasi',
    pathMatch: 'full'
  },
  {
    path: 'gizlilik-politikasi',
    redirectTo: 'dokuman/gizlilik-politikasi',
    pathMatch: 'full'
  },
  {
    path: 'kisisel-veri-gizliligi',
    redirectTo: 'dokuman/kisisel-veri-gizliligi',
    pathMatch: 'full'
  },
  {
    path: 'kullanim-kilavuzu',
    redirectTo: 'dokuman/kullanim-kilavuzu',
    pathMatch: 'full'
  },
  {
    path: 'kullanim-kosullari',
    redirectTo: 'dokuman/kullanim-kosullari',
    pathMatch: 'full'
  },
  {
    path: 'sik-sorulanlar',
    redirectTo: 'dokuman/sik-sorulanlar',
    pathMatch: 'full'
  },
  {
    path: 'teknik-ozellikler',
    redirectTo: 'dokuman/teknik-ozellikler',
    pathMatch: 'full'
  },
  {
    path: 'cerez-politikasi',
    redirectTo: 'dokuman/cerez-politikasi',
    pathMatch: 'full'
  },
  {
    path: 'aydinlatma-metni',
    redirectTo: 'dokuman/aydinlatma-metni',
    pathMatch: 'full'
  },
  {
    path: 'kullanici-sozlesmesi',
    redirectTo: 'dokuman/kullanici-sozlesmesi',
    pathMatch: 'full'
  },
  {
    path: 'unauthorized',
  loadComponent: () => import('./components/shared/unauthorized/unauthorized').then(m => m.UnauthorizedComponent)
  },
  {
    path: '**',
    redirectTo: '/'
  }
];
